'use client'

import React, { useState } from 'react'
import Link from 'next/link'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import useCompanyUsers, { getCompanyUsersQueryKey } from '../../hooks/queries/useCompanyUsers'
import { getCompanySubscriptionQueryKey } from '../../hooks/queries/useCompanySubscription'
import useToggleUserSeat from '../../hooks/mutations/useToggleUserSeat'
import CompanyMember from '../../models/CompanyMember'
import Loader from '../Loader/Loader'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'

type Props = {
	isOpen: boolean
	onDismiss: () => void
	companyId: string | undefined
	onSeatEnabled: () => Promise<void>
}

const EnableSeatDialog = ({ isOpen, onDismiss, companyId, onSeatEnabled }: Props) => {
	const queryClient = useQueryClient()
	const [enablingUserId, setEnablingUserId] = useState<string | null>(null)
	const [retrying, setRetrying] = useState(false)

	const { toggleUserSeat } = useToggleUserSeat()
	const { users, isLoading } = useCompanyUsers(companyId, isOpen)
	const disabledMembers = users.filter((u: CompanyMember) => u.isDisabled)

	const { mutateAsync: enableSeat } = useMutation({
		mutationFn: async (member: CompanyMember) => {
			setEnablingUserId(member.id)
			await toggleUserSeat({ userId: member.id, disabled: false })
			await queryClient.invalidateQueries({ queryKey: getCompanyUsersQueryKey(companyId) })
			setRetrying(true)
			try {
				await onSeatEnabled()
				onDismiss()
			} finally {
				setRetrying(false)
				setEnablingUserId(null)
			}
			await queryClient.invalidateQueries({ queryKey: getCompanySubscriptionQueryKey(companyId) })
		},
		onError: (error: unknown) => {
			setEnablingUserId(null)
			setRetrying(false)
			if (error instanceof Error) {
				toast.error('Something went wrong', {
					description: error.message,
				})
			}
		},
	})

	const handleOpenChange = (open: boolean) => {
		if (!open) onDismiss()
	}

	return (
		<Dialog open={isOpen} onOpenChange={handleOpenChange}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>Enable a seat to continue</DialogTitle>
					<DialogDescription>
						At least one seat must be enabled to start a subscription. Choose a team member below to enable
						their seat, then we&apos;ll continue to checkout.
					</DialogDescription>
				</DialogHeader>
				{isLoading ? (
					<div className="py-6 flex justify-center">
						<Loader />
					</div>
				) : retrying ? (
					<div className="py-6 flex flex-col items-center justify-center gap-2">
						<Loader />
						<p className="text-sm text-muted-foreground">Redirecting to checkout…</p>
					</div>
				) : disabledMembers.length === 0 ? (
					<div className="py-4 space-y-2">
						<p className="text-sm text-muted-foreground">No disabled seats to enable.</p>
						<Link href="/dashboard/members" className="text-sm text-primary hover:underline">
							Go to Members to invite or manage seats
						</Link>
					</div>
				) : (
					<ul className="space-y-2 max-h-60 overflow-y-auto py-2">
						{disabledMembers.map(member => (
							<li
								key={member.id}
								className="flex items-center justify-between gap-4 rounded-md border p-3 text-sm"
							>
								<div>
									<span className="font-medium">
										{member.firstName} {member.lastName}
									</span>
									{member.email && (
										<span className="text-muted-foreground block">{member.email}</span>
									)}
								</div>
								<Button size="sm" onClick={() => enableSeat(member)} disabled={enablingUserId !== null}>
									{enablingUserId === member.id ? 'Enabling…' : 'Enable seat'}
								</Button>
							</li>
						))}
					</ul>
				)}
				<DialogFooter>
					<Button variant="outline" onClick={onDismiss}>
						Cancel
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}

export default EnableSeatDialog
